import { useEffect } from 'react';
import { kurseApi } from '../../../lib/courses';
import { lehrerApi, unterrichtseinheitenApi, type LehrerOption, type UnterrichtseinheitDTO } from '../../../lib/unterrichtseinheiten';
import { getRooms, getRoomsUnits, type Room, type RoomUnit } from '../../../lib/rooms';
import { personenApi, type TeacherAvailabilitySlot } from '../../../lib/personen';
import { fetchTrials, type TrialLessonDTO } from '../../../lib/trials';
import { toApiDate } from '../../../lib/dateFormat';
import type { KursItem } from '../../../lib/courses';
import type { SelectedSlot, SelectedTeilnehmerState, Step1Section, TeilnehmerDetails } from './types';

type UseTrialWizardEffectsArgs = Readonly<{
  open: boolean;
  step: 1 | 2 | 3 | 4 | 5 | 6;
  fachId?: number;
  teacherId?: number;
  selectedCourseId?: number;
  selectedStandortId?: number;
  date: string;
  rooms: Room[];
  selectedSlot: SelectedSlot;
  nextAvailableDate: string;
  teacherUnits: UnterrichtseinheitDTO[];
  teacherDayTrials: TrialLessonDTO[];
  roomId?: number;
  trialRequestPerson?: { firstName?: string; lastName?: string };
  selectedTeilnehmer: SelectedTeilnehmerState | null;
  createHistoryTouched: boolean;
  computedCreateHistoryText: string;
  setStep1ActiveSection: (v: Step1Section) => void;
  setSelectedCourse: (v: KursItem | null) => void;
  setTeacherOptions: (v: LehrerOption[]) => void;
  setRooms: (v: Room[]) => void;
  setRoomUnitsForDate: (v: RoomUnit[]) => void;
  setRoomUnitsLoading: (v: boolean) => void;
  setRoomUnitsError: (v: string) => void;
  setTeacherAvailability: (v: TeacherAvailabilitySlot[]) => void;
  setTeacherAvailabilityLoading: (v: boolean) => void;
  setTeacherUnits: (v: UnterrichtseinheitDTO[]) => void;
  setTeacherDayTrials: (v: TrialLessonDTO[]) => void;
  setTeacherDayLoading: (v: boolean) => void;
  setTeacherDayError: (v: string) => void;
  setNextAvailableDate: (v: string) => void;
  setNextAvailableLoading: (v: boolean) => void;
  setDate: (v: string) => void;
  setStartTime: (v: string) => void;
  setRoomId: (v: number | undefined) => void;
  /** Die Detailanzeige des alten Teilnehmer-Schritts; die Auswahl führt sie selbst. */
  setTeilnehmerDetails?: (v: TeilnehmerDetails | null) => void;
  setCreateHistoryText: (v: string) => void;
  addMinutes: (hhmm: string, minutes: number) => string;
  overlaps: (startA: string, endA: string, startB: string, endB: string) => boolean;
  apiClient: { get: <T>(url: string) => Promise<T> };
}>;

const addDaysYmd = (ymd: string, days: number): string => {
  const d = new Date(`${ymd}T00:00:00`);
  d.setDate(d.getDate() + days);
  return toApiDate(d);
};

const findFirstFreeDate = (
  candidates: string[],
  busyByDate: Map<string, Array<{ s: string; e: string }>>,
  todayYmd: string,
  selStart: string,
  selEnd: string,
  overlaps: UseTrialWizardEffectsArgs['overlaps'],
): string => {
  for (const d of candidates) {
    if (d < todayYmd) continue;
    const busy = busyByDate.get(d) || [];
    const hasOverlap = busy.some((b) => overlaps(selStart, selEnd, b.s, b.e));
    if (!hasOverlap) return d;
  }
  return candidates.find((d) => d >= todayYmd) || '';
};

export const useTrialWizardEffects = ({
  open,
  step,
  fachId,
  teacherId,
  selectedCourseId,
  selectedStandortId,
  date,
  rooms,
  selectedSlot,
  nextAvailableDate,
  teacherUnits,
  teacherDayTrials,
  roomId,
  selectedTeilnehmer,
  createHistoryTouched,
  computedCreateHistoryText,
  setStep1ActiveSection,
  setSelectedCourse,
  setTeacherOptions,
  setRooms,
  setRoomUnitsForDate,
  setRoomUnitsLoading,
  setRoomUnitsError,
  setTeacherAvailability,
  setTeacherAvailabilityLoading,
  setTeacherUnits,
  setTeacherDayTrials,
  setTeacherDayLoading,
  setTeacherDayError,
  setNextAvailableDate,
  setNextAvailableLoading,
  setDate,
  setStartTime,
  setRoomId,
  setTeilnehmerDetails,
  setCreateHistoryText,
  addMinutes,
  overlaps,
  apiClient,
}: UseTrialWizardEffectsArgs) => {
  useEffect(() => {
    if (!open) return;
    if (step !== 1) return;
    if (fachId && teacherId) setStep1ActiveSection('terminplan');
    else setStep1ActiveSection('unterricht');
  }, [open, step, fachId, teacherId, setStep1ActiveSection]);

  useEffect(() => {
    const loadCourse = async () => {
      if (!open || !selectedCourseId) {
        setSelectedCourse(null);
        return;
      }
      try {
        const course = await kurseApi.get(selectedCourseId);
        setSelectedCourse(course);
      } catch {
        setSelectedCourse(null);
      }
    };
    loadCourse();
  }, [open, selectedCourseId, setSelectedCourse]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const fId = fachId && fachId > 0 ? fachId : undefined;
    const sId = selectedStandortId && selectedStandortId > 0 ? selectedStandortId : undefined;
    lehrerApi.list(fId || sId ? { fachId: fId, standortId: sId } : undefined)
      .then((list) => {
        if (cancelled) return;
        // Das Backend filtert nach Fach und Standort (nur zugeordnete Lehrer).
        const activeTeachers = (list || [])
          .filter((teacher) => teacher.aktiv !== false)
          .sort((a, b) => `${a.vorname || ''} ${a.nachname || ''}`.localeCompare(`${b.vorname || ''} ${b.nachname || ''}`, 'de'));
        setTeacherOptions(activeTeachers);
      })
      .catch(() => {
        if (cancelled) return;
        setTeacherOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, fachId, selectedStandortId, setTeacherOptions]);

  useEffect(() => {
    if (!open) return;
    getRooms(selectedStandortId ?? undefined).then(setRooms).catch(() => setRooms([]));
  }, [open, selectedStandortId, setRooms]);

  useEffect(() => {
    const fetchUnits = async () => {
      if (!open || !date) {
        setRoomUnitsForDate([]);
        return;
      }
      setRoomUnitsLoading(true);
      setRoomUnitsError('');
      try {
        const ids = rooms.map((r) => r.id);
        if (ids.length === 0) {
          setRoomUnitsForDate([]);
          return;
        }
        const units = await getRoomsUnits(ids, date, date);
        setRoomUnitsForDate(units);
      } catch {
        setRoomUnitsForDate([]);
        setRoomUnitsError('Raumbelegung konnte nicht geladen werden.');
      } finally {
        setRoomUnitsLoading(false);
      }
    };
    fetchUnits();
  }, [open, rooms.map((r) => r.id).join(','), date, setRoomUnitsError, setRoomUnitsForDate, setRoomUnitsLoading]);

  useEffect(() => {
    const loadAvailability = async () => {
      if (!open || !teacherId || !selectedStandortId) {
        setTeacherAvailability([]);
        return;
      }
      setTeacherAvailabilityLoading(true);
      try {
        const slots = await personenApi.getTeacherAvailability(teacherId, selectedStandortId);
        setTeacherAvailability(slots || []);
      } catch {
        setTeacherAvailability([]);
      } finally {
        setTeacherAvailabilityLoading(false);
      }
    };
    loadAvailability();
  }, [open, teacherId, selectedStandortId, setTeacherAvailability, setTeacherAvailabilityLoading]);

  useEffect(() => {
    const loadTeacherDayData = async () => {
      if (!open || !teacherId || !date) {
        setTeacherUnits([]);
        setTeacherDayTrials([]);
        return;
      }
      setTeacherDayLoading(true);
      setTeacherDayError('');
      try {
        const [units, trials] = await Promise.all([
          unterrichtseinheitenApi.list({ teacherId, start: date, end: date }),
          fetchTrials(date, date),
        ]);
        setTeacherUnits(units);
        const sameTeacherTrials = (trials || []).filter((t) => String(t.teacherId || '') === String(teacherId));
        setTeacherDayTrials(sameTeacherTrials);
      } catch {
        setTeacherUnits([]);
        setTeacherDayTrials([]);
        setTeacherDayError('Lehrerbelegungen konnten nicht geladen werden.');
      } finally {
        setTeacherDayLoading(false);
      }
    };
    loadTeacherDayData();
  }, [open, teacherId, date, setTeacherDayError, setTeacherDayLoading, setTeacherDayTrials, setTeacherUnits]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!open) return;
      if (!selectedSlot || !teacherId) {
        setNextAvailableDate('');
        return;
      }

      setNextAvailableLoading(true);
      try {
        const todayYmd = toApiDate(new Date());
        const endDate = addDaysYmd(todayYmd, 56);
        const startDate = addDaysYmd(todayYmd, -28);

        const candidates: string[] = [];
        const start = new Date(`${startDate}T00:00:00`);
        const end = new Date(`${endDate}T00:00:00`);
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const cand = toApiDate(d);
          const dow = ((d.getDay() + 6) % 7) + 1;
          if (dow === selectedSlot.dayOfWeek) candidates.push(cand);
        }

        const [units, trials] = await Promise.all([
          unterrichtseinheitenApi.list({ teacherId, start: todayYmd, end: endDate }),
          fetchTrials(todayYmd, endDate),
        ]);
        const busyByDate = new Map<string, Array<{ s: string; e: string }>>();
        (units || []).forEach((u) => {
          const ymd = u.start_zeit.slice(0, 10);
          const s = u.start_zeit.slice(11, 16);
          const e = u.end_zeit.slice(11, 16);
          const arr = busyByDate.get(ymd) || [];
          arr.push({ s, e });
          busyByDate.set(ymd, arr);
        });
        (trials || [])
          .filter((t) => String(t.teacherId || '') === String(teacherId))
          .forEach((t) => {
            const ymd = t.date;
            const s = t.startTime.slice(0, 5);
            const e = t.endTime.slice(0, 5);
            const arr = busyByDate.get(ymd) || [];
            arr.push({ s, e });
            busyByDate.set(ymd, arr);
          });

        const selStart = selectedSlot.startTime.slice(0, 5);
        const selEnd = selectedSlot.endTime.slice(0, 5) || addMinutes(selStart, 30);
        const firstFree = findFirstFreeDate(candidates, busyByDate, todayYmd, selStart, selEnd, overlaps);
        if (!cancelled) setNextAvailableDate(firstFree);
      } catch {
        if (!cancelled) setNextAvailableDate('');
      } finally {
        if (!cancelled) setNextAvailableLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [open, teacherId, selectedSlot?.dayOfWeek, selectedSlot?.startTime, selectedSlot?.endTime, addMinutes, overlaps, setNextAvailableDate, setNextAvailableLoading]);

  useEffect(() => {
    if (!open) return;
    if (step !== 2) return;
    if (!selectedSlot) return;
    if (!nextAvailableDate) return;
    if (!date) {
      setDate(nextAvailableDate);
      setStartTime(selectedSlot.startTime.slice(0, 5));
    }
  }, [open, step, selectedSlot, nextAvailableDate, date, setDate, setStartTime]);

  useEffect(() => {
    if (!open) return;
    if (step !== 3) return;
    if (roomId) return;
    if (!date) return;
    const counts = new Map<number, number>();
    const inc = (id: number) => counts.set(id, (counts.get(id) || 0) + 1);
    teacherUnits.forEach((u) => { if (u.raum_id) inc(Number(u.raum_id)); });
    teacherDayTrials.forEach((t) => { if (t.roomId) inc(Number(t.roomId)); });
    const best = Array.from(counts.entries()).sort((a, b) => b[1] - a[1])[0]?.[0];
    if (best) setRoomId(best);
  }, [open, step, roomId, date, teacherUnits, teacherDayTrials, setRoomId]);

  useEffect(() => {
    const loadTeilnehmerDetails = async () => {
      if (!selectedTeilnehmer?.id || !selectedTeilnehmer?.parentId) {
        setTeilnehmerDetails?.(null);
        return;
      }
      try {
        const details = await apiClient.get<TeilnehmerDetails>(`/api/personen/${selectedTeilnehmer.parentId}/subprofiles/${selectedTeilnehmer.id}`);
        setTeilnehmerDetails?.(details);
      } catch {
        setTeilnehmerDetails?.(null);
      }
    };
    if (selectedTeilnehmer?.parentId) {
      loadTeilnehmerDetails();
    }
  }, [selectedTeilnehmer?.id, selectedTeilnehmer?.parentId, setTeilnehmerDetails, apiClient]);

  useEffect(() => {
    if (!open) return;
    if (createHistoryTouched) return;
    setCreateHistoryText(computedCreateHistoryText);
  }, [open, createHistoryTouched, computedCreateHistoryText, setCreateHistoryText]);
};
